"use client";
import { Fragment, useContext } from "react";
import Link from "next/link";
import { useEffect, useState, useRef, useCallback } from "react";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { createBrowserSupabaseClient } from '@/utility/supabase/browser';
import { Provider } from "@supabase/supabase-js";
import { SpinnerCircularFixed } from "spinners-react";
import { useRouter } from "next/navigation";
import { RequestError } from "@/utility/custom-error";
import { LenisContext } from "@/context/lenis-context";
import { v4 as uuidv4 } from "uuid";
import { isApplicationTrafficReleased } from '@/utility/maintenance-client';

// components
import InlineInput from "../inputs/InlineInput";
import clsx from 'clsx';
import buttonStyles from '@/components/Button/Button.module.scss';

// import media
import { ReactComponent as GoogleLogo } from "@/public/svgs/google-logo.svg";
import { ReactComponent as LinkedinLogo } from "@/public/svgs/linkedin-logo.svg";
import { ReactComponent as AppleLogo } from "@/public/svgs/apple-logo.svg";
import { ReactComponent as FacebookLogo } from '@/public/svgs/facebook-logo.svg';
import CustomWait from "@/utility/custom-wait";
import { loginFormStyles as styles } from '@/components/LoginForm';

const validationSchema = yup.object().shape(
  {
    email: yup.string()
      .email("Please enter a valid email")
      .required("Email is required."),
    password: yup.string()
      .min(6, "Password must be at least 6 characters.")
      .required("Password is required."),
  },
);

export default function ClientForm({
  type,
  googleLogin,
  linkedinLogin,
  className,
}: {
  type: "login" | "signup";
  googleLogin?: boolean;
  linkedinLogin?: boolean;
  className?: string;
})
{
  // router
  const router = useRouter();

  // context
  const lenisContext = useContext(LenisContext);

  // state
  const [isLoading, setIsLoading] = useState<{ loading: boolean, message: string; }>({
    loading: false,
    message: ''
  });
  const [signupComplete, setSignupComplete] = useState<boolean>(false);

  // supabase
  const supabase = createBrowserSupabaseClient('consumer');

  // refs
  const isMountedRef = useRef<boolean>(false);

  useEffect(() =>
  {
    isMountedRef.current = true;

    return () =>
    {
      isMountedRef.current = false;
    };

  }, []);

  const handleSubmit = useCallback((values: { email: string; password: string; }) =>
  {
    switch (type)
    {
      case "login":
        HandleSignInWithEmail(values);
        break;
      case "signup":
        HandleSignUpWithEmail(values);
        break;
    }
  }, []);


  const HandleOAuthLogin = useCallback(async (provider: Provider) =>
  {
    if (!await isApplicationTrafficReleased()) {
      location.reload();
      return;
    }
    if (provider === 'linkedin_oidc')
      setIsLoading({
        loading: true, message: `
    First time logging in with LinkedIn?<br>
    Please wait while we set up your account.
      `
      });

    await supabase.auth.signInWithOAuth({
      provider,
      options:
      {
        redirectTo: `${ location.origin }/auth/callback`,
      }
    });
  }, []);

  const HandleSignInWithEmail = async (
    values: { email: string; password: string; },
  ) =>
  {
    if (!await isApplicationTrafficReleased()) {
      location.reload();
      return;
    }
    setIsLoading({ loading: true, message: 'Logging in...' });

    try
    {
      const response = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      // redirect
      if (response.data.user)
        router.push(`${ location.origin }`);
      else
        throw new RequestError("User not found", 404);

    } catch (err: any)
    {
      if (err instanceof RequestError)
      {
        lenisContext.addToast({
          id: uuidv4(),
          message: err.message,
          type: 'error',
          statusCode: err.statusCode
        });
      }

      isMountedRef.current && setIsLoading({ loading: false, message: '' });
    }
  };

  const HandleSignUpWithEmail = async (
    values: { email: string; password: string; },
  ) =>
  {

    if (!await isApplicationTrafficReleased()) {
      location.reload();
      return;
    }

    try
    {
      const data = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          emailRedirectTo: `${ location.origin }/login`
        }
      });

      // if error throw data and access error in catch block
      if (data.error)
        throw data;

      isMountedRef.current && setSignupComplete(true);

    } catch (err: any)
    {
      if (err.error)
      {
        lenisContext.addToast({
          id: uuidv4(),
          message: 'User with this email already exists.',
          type: 'error',
          statusCode: 409
        });
      }
      else
      {
        lenisContext.addToast({
          id: uuidv4(),
          message: 'Unknown sign up error occurred, please try again later.',
          type: 'error',
          statusCode: 500
        });
      }
    }
  };

  return (
    <Formik
      initialValues={ {
        email: "",
        password: "",
      } }
      validationSchema={ validationSchema }
      enableReinitialize
      onSubmit={ (values) => handleSubmit(values) }
      validateOnBlur
    >
      { ({ values, errors, touched, handleChange, handleBlur }) =>
      {
        return (
          <div className={clsx(styles.loginForm, className)}>

            <Form>

              { isLoading.loading &&
                <div className={styles.loader}>
                  <div className={styles.wrapper}>
                    <p dangerouslySetInnerHTML={ { __html: isLoading.message } }>
                    </p>
                    <SpinnerCircularFixed />
                  </div>
                </div>
              }

              {/* Email field */ }
              { false &&
                <>
                  <InlineInput
                    disabled={ signupComplete }
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Email"
                    value={ values.email }
                    onChange={ handleChange }
                    onBlur={ handleBlur }
                    error={ touched.email ? errors.email : "" }
                  />

                  <InlineInput
                    disabled={ signupComplete }
                    type="password"
                    name="password"
                    id="password"
                    placeholder="Password"
                    value={ values.password }
                    onChange={ handleChange }
                    onBlur={ handleBlur }
                    error={ touched.password ? errors.password : "" }
                    isPassword />

                  <button
                    disabled={ signupComplete }
                    className={ clsx(buttonStyles.bigButton, buttonStyles.bigButtonBlack, styles.bigButton) }
                    type="submit">
                    { type === "login" ? "Log In" : "Sign Up" }
                  </button>

                  { signupComplete &&
                    <p style={ { marginBottom: '3.2rem', textAlign: 'center' } } className={ styles.successMessage }>
                      Sign up completed. Please check your email to verify your account!
                    </p>
                  }


                  { type === "login"
                    ? (
                      <Fragment>
                        <Link
                          href="/signup"
                          className={styles.formPrompt}>
                          New User? <span>Create an account</span>
                        </Link>
                        <Link
                          href="/forgot-password"
                          className={styles.formPrompt}>
                          Forgot your password? <span>Reset it here</span>
                        </Link>
                      </Fragment>

                    )
                    : (
                      <Fragment>
                        <Link
                          href="/login"
                          className={styles.formPrompt}>
                          Already have an account? <span>Back to login</span>
                        </Link>
                        <Link
                          href="/forgot-password"
                          className={styles.formPrompt}>
                          Forgot your password? <span>Reset it here</span>
                        </Link>
                      </Fragment>
                    ) }

                  <div className={styles.separator}>
                    <span></span>
                    <p>or</p>
                    <span></span>
                  </div>
                </>

              }

              {/* Google */ }
              { googleLogin != undefined && googleLogin &&
                <button
                  type="button"
                  className={ clsx(buttonStyles.logoButton, buttonStyles.logoButtonRounded, styles.logoButton) }
                  onClick={ () => HandleOAuthLogin('google') }>
                  <div className={ buttonStyles.logo }>
                    <GoogleLogo />
                  </div>
                  Continue with Google
                </button>
              }

              { googleLogin === undefined &&
                <button
                  type="button"
                  className={ clsx(buttonStyles.logoButton, buttonStyles.logoButtonRounded, styles.logoButton) }
                  onClick={ () => HandleOAuthLogin('google') }>
                  <div className={ buttonStyles.logo }>
                    <GoogleLogo />
                  </div>
                  Continue with Google
                </button>
              }

              {/* Facebook */ }
              {/* <button
              type="button"
              className={ clsx(buttonStyles.logoButton, buttonStyles.logoButtonRounded, styles.logoButton) }
              onClick={ () => HandleOAuthLogin('facebook') }>
              <div className={ buttonStyles.logo }>
                <FacebookLogo />
              </div>
              Continue with Facebook
            </button> */}

              {/* Linkedin */ }
              { linkedinLogin != undefined && linkedinLogin &&
                <button
                  type="button"
                  className={ clsx(buttonStyles.logoButton, buttonStyles.logoButtonRounded, styles.logoButton) }
                  onClick={ () => HandleOAuthLogin('linkedin_oidc') }>
                  <div className={ buttonStyles.logo }>
                    <LinkedinLogo />
                  </div>
                  Continue with Linkedin
                </button>
              }

              { linkedinLogin === undefined &&
                <button
                  type="button"
                  className={ clsx(buttonStyles.logoButton, buttonStyles.logoButtonRounded, styles.logoButton) }
                  onClick={ () => HandleOAuthLogin('linkedin_oidc') }>
                  <div className={ buttonStyles.logo }>
                    <LinkedinLogo />
                  </div>
                  Continue with Linkedin
                </button>
              }

              {/* Apple */ }
              {
                /* <button
                                  type="button"
                                  className={ clsx(buttonStyles.logoButton, buttonStyles.logoButtonRounded, buttonStyles.logoButtonBlackSvg, styles.logoButton) }>
                                  <div className={ buttonStyles.logo }>
                                      <AppleLogo />
                                  </div>
                                  Continue with Apple
                              </button> */
              }

            </Form>

          </div>
        );
      } }
    </Formik >
  );
};
